import { Component, OnInit, HostBinding, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewEncapsulation } from "@angular/core";
import {
  ConditionMonitoringService,
  ICMEquipmentAlerts,
  IDisplayVehicle,
  ISelectedPosition,
} from "../condition-monitoring.service";
import { XlatPipe } from "../../../../amt/i18n/xlat-pipe";
import { Inject } from "@angular/core";
import { SelectEvent } from "@progress/kendo-angular-layout";
import { Observable, Subject } from "rxjs";
import { VisualInspectionService } from "../../../../services/visual-inspection.service";
import { IVisualInspectionInspection } from "../../../../interfaces/visualInspection.interfaces";
import { IConditonMonitoringTabs } from "@Models/condition-monitoring/IConditionMonitoringTabs";
import { IRendererConfig } from "../monitoringOptions/renderer-config-provider/IRendererConfig";
import { take, takeUntil, tap } from "rxjs/operators";

export const ICON_WIDTH = 196;
export const ICON_HEIGHT = 196;

interface IGroup<T> {
  name: string;
  members: T[];
}
export const amtConstants = {
  emptyGuid: "00000000-0000-0000-0000-000000000000",
};
@Component({
  selector: "condition-monitoring-details",
  templateUrl: "./condition-monitoring-details.component.html",
  styleUrls: ["./condition-monitoring-details.component.scss"],
  providers: [
    { provide: "desiredWidth", useValue: ICON_WIDTH },
    { provide: "desiredHeight", useValue: ICON_HEIGHT },
    ConditionMonitoringService,
  ],
})
export class ConditionMonitoringDetailsComponent implements OnInit, OnDestroy {
  selectedTab: number = 0;
  tabNames: string[];
  title: string;
  @Input() wnd;
  @Input() initParams;
  tabs: IConditonMonitoringTabs[] = [];
  vehicle: IDisplayVehicle;
  rendererConfig: IRendererConfig;
  currentVehicle$: Observable<any>;
  currentVehicle: any;
  selectedComponent: ISelectedPosition;
  isProcessing: boolean = false;
  isProcessingTabs: boolean[] = [];
  selectedInspection: IVisualInspectionInspection;
  plannedWorkDetails = this.xlatPipe.transform(
    "conditionmonitoring.tab_PlannedWork"
  );
  comingSoon = this.xlatPipe.transform("common.comingSoon");

  //Security permissions
  canAddFieldSurvey: boolean = false;
  canAddMaintenanceSession: boolean = false;
  canViewComponent: boolean = false;
  canViewVehicle: boolean = false;

  onDestroy = new Subject<void>();

  constructor(
    public monSvc: ConditionMonitoringService,
    private visualInspectionService: VisualInspectionService,
    private xlatPipe: XlatPipe,
    @Inject("ocSecuritySvc") private ocSecuritySvc: IOcSecuritySvc
  ) {
    this.title = xlatPipe.transform("DashboardWidget.ConditionMonitoring");
  }

  @HostBinding("style.--img-w") public readonly cssImgWidth = ICON_WIDTH;
  @HostBinding("style.--img-h") public readonly cssImgHeight = ICON_HEIGHT;

  get hasTabs() { return this.tabs?.length; }

  ngOnInit(): void {
    const { vehicle, tabs, rendererConfig } = this.initParams;
    this.isProcessing = !this.isProcessingTabs[this.selectedTab];
    (this.currentVehicle$ = this.monSvc.getVehicle(vehicle.equipmentId).pipe(
      take(1),
      takeUntil(this.onDestroy),
      tap((currentVehicle : {vehicleAlerts: ICMEquipmentAlerts[], vehicleWear: ICMEquipmentAlerts[]}) => {
        const { vehicleAlerts, vehicleWear } = currentVehicle;
        const config = this.rendererConfig?.issues || [];
        vehicleAlerts.forEach((alert) => {
          this.monSvc.assignAlertIssue(alert, config);
        });
        vehicleWear.forEach((alert) => {
          this.monSvc.assignAlertIssue(alert, config);
        });
      }),
    )).subscribe((currentVehicle: any) => {
      this.currentVehicle = currentVehicle;
    });
    this.vehicle = vehicle;
    this.rendererConfig = rendererConfig;

    this.tabNames = tabs.map(tab => tab.title);
    while (this.hasTabs) this.tabs.pop();
    this.tabs.push(...tabs);

      //Setup permissions
      this.canAddFieldSurvey = this.ocSecuritySvc.isAuthorised('Security.Vehicles.FieldSurveys.Add', AccessTypes.readWrite);
      this.canAddMaintenanceSession = this.ocSecuritySvc.isAuthorised('Security.Vehicles.MaintenanceSession.Add', AccessTypes.readWrite);
      this.canViewComponent = this.ocSecuritySvc.isAuthorised('Security.Components', AccessTypes.readOnly);
      this.canViewVehicle = this.ocSecuritySvc.isAuthorised('Security.Vehicles', AccessTypes.readOnly);

      this.wnd.buttonStates = {
          // Action buttons don't have dynamic disable/enable, don't bother including it
          createFieldSurveyButton: {
              visible: false
          },
          addMaintenanceSessionButton: {
              visible: false
          },
          viewComponentDetailsButton: {
              visible: false
          },
          viewVehicleDetailsButton: {
              visible: false
          }
      };
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  tabSelected(index: number): boolean {
    return this.tabs.find(tab => tab.title === this.tabNames[index])?.selected || false;
  }
  
  getTabType(index: number) : number {
    switch(this.tabs[index]?.type) {
      case 'conditionDetails':
        return 0;
      case 'visualInspection':
        return 1;
      case 'treadWear':
        return 2;
      default:
        return -1;
    }
  }

    updateSelectedComponent(component: ISelectedPosition) {
        this.selectedComponent = component;
        this.buttonConfiguration();
    }

  onDoneProcessing(done: boolean) {
      if (done) {
        this.isProcessingTabs[this.selectedTab] = true;
        this.isProcessing = false;
      }

      this.buttonConfiguration();
  }

    public onTabSelect(e: SelectEvent): void {
      if (this.selectedTab === e.index)
          return;

      this.selectedTab = e.index;
      this.selectedComponent = null; //CLear the selected component as we are switching tabs

      //Don't set processing for Planned Work tab as there is nothing that currently processes.
        if (e.index !== 3) {
            this.isProcessing = true;
        } else {
            //Hide all the action buttons
            this.wnd.buttonStates.createFieldSurveyButton.visible = false;
            this.wnd.buttonStates.addMaintenanceSessionButton.visible = false;
            this.wnd.buttonStates.viewComponentDetailsButton.visible = false;
            this.wnd.buttonStates.viewVehicleDetailsButton.visible = false;

            this.wnd.buttonMethods = [];
        }
  }

  openMaintenanceSession = () => {
      this.monSvc.openMaintenanceSession(this.vehicle);
  }

  showVehicle = () => {
        this.monSvc.showVehicle(this.vehicle, this.currentVehicle);
  }

    showComponentDetails = () => {
        this.monSvc.showComponent(this.selectedComponent, false);
    };

    addFieldSurvey = () => {
        if (this.vehicle.equipmentId)
            this.visualInspectionService.addFieldSurvey(this.vehicle.equipmentId, this.selectedInspection?.date);
    };

    private buttonConfiguration() {
        if (this.canAddFieldSurvey) {
            this.wnd.buttonStates.createFieldSurveyButton.visible = true;
            this.wnd.buttonMethods.addFieldSurvey = this.addFieldSurvey;
        }

        if (this.canAddMaintenanceSession) {
            this.wnd.buttonStates.addMaintenanceSessionButton.visible = true;
            this.wnd.buttonMethods.openMaintenanceSession = this.openMaintenanceSession;
        }

        if (this.selectedComponent && this.canViewComponent) {
            this.wnd.buttonStates.viewComponentDetailsButton.visible = true;
            this.wnd.buttonMethods.showComponentDetails = this.showComponentDetails;
        } else {
            this.wnd.buttonStates.viewComponentDetailsButton.visible = false;
            this.wnd.buttonMethods.showComponentDetails = null;
        }

        if (this.canViewVehicle) {
            this.wnd.buttonStates.viewVehicleDetailsButton.visible = true;
            this.wnd.buttonMethods.showVehicle = this.showVehicle;
        }

        this.wnd.buttons = [
            {
                title: 'fieldSurvey.create',
                click: 'addFieldSurvey',
                name: 'createFieldSurveyButton',
                type: 2
            },
            {
                title: 'equipment.maintenanceSession',
                click: 'openMaintenanceSession',
                name: 'addMaintenanceSessionButton',
                type: 2
            },
            {
                title: 'visualInspection.openComponentDetails',
                click: 'showComponentDetails',
                name: 'viewComponentDetailsButton',
                type: 2
            },
            {
                title: 'visualInspection.openVehicleDetails',
                click: 'showVehicle',
                name: 'viewVehicleDetailsButton',
                type: 2
            }
        ];
    }

}

